/* eslint-disable @typescript-eslint/no-unsafe-call */
import { addBreadCrumb, captureException } from '~/services/sentry'
import {
  buildEnvironment,
  cloudwatchLogGroup,
  getCloudwatchLogGroupName,
} from '~/utils/common'

import { isSipLoggingDisable } from '@mangovoice/sip/core'
import { Logger as ILogger } from '@mangovoice/types'

import CloudWatchLogger from './CloudwatchLogger'
import { formatConsoleMessage, LogLevel } from './Logger.console'

export interface CloudwatchMetadata {
  pbxId: number
  userId: number
}

class Logger implements ILogger {
  private tag: string
  private cloudwatchMetadata?: CloudwatchMetadata

  constructor(tag: string, metadata?: CloudwatchMetadata) {
    this.tag = tag
    this.cloudwatchMetadata = metadata
  }

  private log(
    level: LogLevel,
    message: string,
    error: Error | null = null,
    args?: Record<string, unknown>,
  ): void {
    const isProduction = buildEnvironment === 'production'
    const logMessage = formatConsoleMessage(this.tag, level, message, error, {
      ...args,
    })

    if (!isProduction && !isSipLoggingDisable()) {
      console.log(logMessage)
    }

    switch (level) {
      case LogLevel.DEBUG:
        // do nothing in production
        break
      case LogLevel.INFO:
        addBreadCrumb({
          level: 'info',
          category: this.tag,
          message,
          data: args,
        })
        break
      case LogLevel.WARN:
        addBreadCrumb({
          level: 'warning',
          category: this.tag,
          message,
          data: args,
        })
        break
      case LogLevel.ERROR:
        captureException(error ?? new Error(message), args)
        break
      default:
        break
    }

    if (this.cloudwatchMetadata && level !== LogLevel.DEBUG) {
      const timestamp = new Date().toISOString()
      const cwMessage = `${timestamp} | ${this.tag} | ${level} | ${message}`
      CloudWatchLogger.getInstance(
        cloudwatchLogGroup,
        `pbx/${this.cloudwatchMetadata.pbxId}/user/${this.cloudwatchMetadata.userId}/user.log`,
      ).log(cwMessage)
    }
  }

  /**
   * Writes to console in __DEV__ mode, does not send to Sentry
   * Swallows everything in __PROD__ mode
   * @param message the string to log
   */
  public debug(message: string, args?: Record<string, unknown>): void {
    this.log(LogLevel.DEBUG, message, null, args)
  }

  /**
   * Writes to console in __DEV__ mode
   * Adds a Sentry Breadcrumb in __PROD__ mode
   * @param message string to log
   */
  public info(message: string, args?: Record<string, unknown>): void {
    this.log(LogLevel.INFO, message, null, args)
  }

  /**
   * Writes to console in __DEV__ mode
   * @param message string to log
   */
  public warn(message: string, args?: Record<string, unknown>): void {
    this.log(LogLevel.WARN, message, null, args)
  }

  /**
   * Writes to console in __DEV__ mode
   * Sends to Sentry in __PROD__ mode
   * @param message
   * @param error
   */
  public error(
    message: string,
    error: Error | null = null,
    args?: Record<string, unknown>,
  ): void {
    this.log(LogLevel.ERROR, message, error, args)
  }

  public async flush(): Promise<void> {
    if (this.cloudwatchMetadata) {
      await CloudWatchLogger.getInstance(
        getCloudwatchLogGroupName(),
        `pbx/${this.cloudwatchMetadata.pbxId}/user/${this.cloudwatchMetadata.userId}/user.log`,
      ).flush()
    }
  }
}

export default Logger
